import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, CollapsibleSection } from '@roger/ui-components';
import { Zap } from 'lucide-react';
import type { EligibilityCheckResponse } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import {
  EMPTY_PATIENT_INSURANCE_FORM,
  parseOptionalFloat,
  PatientInsuranceFields,
  type PatientInsuranceFormState,
} from '../patient/PatientInsuranceFields';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { formatSSN, normalizeGender } from '../../utils/intake';

/** Keys the intake section renders (payer + member ID stay bound to the
 * dedicated Eligibility tab controls above the section). */
const HIDDEN_FIELDS = ['agency_insurance_id', 'policy_number'] as const;

type SectionKey = Exclude<
  keyof PatientInsuranceFormState,
  (typeof HIDDEN_FIELDS)[number]
>;

const DATE_KEYS: ReadonlySet<SectionKey> = new Set([
  'insured_dob',
  'effective_start',
  'effective_end',
]);
const NUMBER_KEYS: ReadonlySet<SectionKey> = new Set([
  'deductible',
  'coverage_percent',
  'copay_amount',
]);
const BOOLEAN_KEYS: ReadonlySet<SectionKey> = new Set([
  'assignment_of_benefits',
  'insured_signature_on_file',
]);
/** Free-text keys autosave on a debounce; everything else saves immediately. */
const DEBOUNCED_KEYS: ReadonlySet<SectionKey> = new Set([
  'insured_last_name',
  'insured_first_name',
  'insured_ssn',
  'insured_phone',
  'insured_employer',
  'plan_name',
  'insured_address',
  'insured_city',
  'insured_zip',
  'group_name',
  'group_number',
  'deductible',
  'coverage_percent',
  'copay_amount',
]);

/** Serialize one form value into its JSONB blob representation. Empty string
 * serializes to null ("explicitly blank" — no fallback at onboarding). */
function serializeFieldValue(
  key: SectionKey,
  value: PatientInsuranceFormState[SectionKey]
): unknown {
  if (DATE_KEYS.has(key)) {
    return value instanceof Date ? formatDateToISO(value) : null;
  }
  if (BOOLEAN_KEYS.has(key)) {
    return !!value;
  }
  if (NUMBER_KEYS.has(key)) {
    return parseOptionalFloat(String(value ?? ''));
  }
  if (key === 'insured_ssn') {
    return String(value ?? '').replace(/\D/g, '') || null;
  }
  return String(value ?? '').trim() || null;
}

/** Deserialize one JSONB blob value back into its form representation. */
function deserializeFieldValue(
  key: SectionKey,
  value: unknown
): PatientInsuranceFormState[SectionKey] {
  if (DATE_KEYS.has(key)) {
    return (
      typeof value === 'string' && value
        ? (parseISOToLocalDate(value) ?? undefined)
        : undefined
    ) as PatientInsuranceFormState[SectionKey];
  }
  if (BOOLEAN_KEYS.has(key)) {
    return (value === true) as PatientInsuranceFormState[SectionKey];
  }
  if (NUMBER_KEYS.has(key)) {
    return (
      value != null ? String(value) : ''
    ) as PatientInsuranceFormState[SectionKey];
  }
  if (key === 'insured_ssn') {
    return (
      typeof value === 'string' && value ? formatSSN(value) : ''
    ) as PatientInsuranceFormState[SectionKey];
  }
  return (
    value != null ? String(value) : ''
  ) as PatientInsuranceFormState[SectionKey];
}

/** Subset of the Eligibility tab's editable form the prefill live-syncs with. */
export interface IntakeInsurancePrefillFormData {
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string;
  patient_street_address: string;
  patient_city: string;
  patient_state: string;
  patient_zip_code: string;
}

interface IntakeInsuranceSectionProps {
  selectedIntake: IntakeRecord | null;
  formData: IntakeInsurancePrefillFormData;
  pastEligibilityResults: EligibilityCheckResponse[];
  saveField: (fieldName: string, value: unknown) => Promise<unknown>;
  debouncedSave: (fieldName: string, value: unknown, delay?: number) => void;
  cancelDebounce: (fieldName: string) => void;
  hasPendingDebounce: (fieldName: string) => boolean;
}

/**
 * Patient Insurance section on the intake Eligibility tab (EMR-886).
 *
 * Sparse-override editor over `intake.fields["patient_insurance"]`: untouched
 * fields display a live prefill derived from the intake's patient demographics
 * (matching what push-to-Roger onboarding would create implicitly), and a field
 * only lands in the saved blob once the user edits it. Every save replaces the
 * whole blob (top-level JSONB merge semantics on the backend).
 */
export const IntakeInsuranceSection: React.FC<IntakeInsuranceSectionProps> = ({
  selectedIntake,
  formData,
  pastEligibilityResults,
  saveField,
  debouncedSave,
  cancelDebounce,
  hasPendingDebounce,
}) => {
  // The parent remounts this component per intake (key={intake.id}), so the
  // lazy state initializers run fresh for each record and one record's
  // unsaved edits never bleed into the next.
  const [overrides, setOverrides] = useState<Record<string, unknown>>(
    () => (selectedIntake?.patient_insurance ?? {}) as Record<string, unknown>
  );
  // Raw form values exactly as edited this session. They win over the
  // serialized override for display, so typing never round-trips through the
  // lossy blob encoding (which trims strings and parses numbers — eating
  // trailing spaces and intermediate states like "12.").
  const [draft, setDraft] = useState<
    Partial<Pick<PatientInsuranceFormState, SectionKey>>
  >({});
  // Latest overrides for the handlers. Written in the handlers alongside
  // setOverrides — never during render, which is unsafe under StrictMode and
  // concurrent rendering (a discarded render could leave the ref pointing at
  // state that was never committed).
  const overridesRef = useRef(overrides);

  // Derived display form: override wins per key; untouched keys show the live
  // prefill (kept in sync with Patient Information edits on this tab and
  // matching the backend's implicit-derivation fallbacks, so what you see is
  // what onboarding creates).
  const displayForm = useMemo<PatientInsuranceFormState>(() => {
    const prefill: PatientInsuranceFormState = {
      ...EMPTY_PATIENT_INSURANCE_FORM,
      position: 'Primary',
      relation_to_insured: 'Self',
      assignment_of_benefits: true,
      insured_signature_on_file: true,
      insured_first_name: formData.patient_first_name || '',
      insured_last_name: formData.patient_last_name || '',
      insured_dob: formData.patient_dob
        ? (parseISOToLocalDate(formData.patient_dob) ?? undefined)
        : undefined,
      insured_address: formData.patient_street_address || '',
      insured_city: formData.patient_city || '',
      insured_state: (formData.patient_state || '').trim().toUpperCase(),
      insured_zip: formData.patient_zip_code || '',
      insured_gender: normalizeGender(selectedIntake?.patient_gender) ?? '',
      insured_ssn: selectedIntake?.patient_ssn
        ? formatSSN(selectedIntake.patient_ssn)
        : '',
      insured_phone: selectedIntake?.patient_phone || '',
    };

    const form = { ...prefill };
    for (const key of Object.keys(form) as SectionKey[]) {
      if (key in draft) {
        (form as Record<string, unknown>)[key] = draft[key];
      } else if (key in overrides) {
        (form as Record<string, unknown>)[key] = deserializeFieldValue(
          key,
          overrides[key]
        );
      }
    }
    return form;
  }, [draft, formData, overrides, selectedIntake]);

  // Local cross-field validation: while the effective-date range is invalid,
  // edits stay local and saving pauses.
  const dateRangeInvalid =
    displayForm.effective_start instanceof Date &&
    displayForm.effective_end instanceof Date &&
    displayForm.effective_start >= displayForm.effective_end;

  // Onboarding's normalize_ssn stores NULL unless the SSN is exactly 9
  // digits — warn so the user doesn't see a value that never lands.
  const ssnDigits = displayForm.insured_ssn.replace(/\D/g, '');
  const ssnIncomplete = ssnDigits.length > 0 && ssnDigits.length !== 9;

  const errors = useMemo(() => {
    const keys = [
      ...(dateRangeInvalid ? ['effective_start', 'effective_end'] : []),
      ...(ssnIncomplete ? ['insured_ssn'] : []),
    ];
    return keys.length ? new Set(keys) : undefined;
  }, [dateRangeInvalid, ssnIncomplete]);

  const persist = useCallback(
    (next: Record<string, unknown>, debounced: boolean) => {
      const start = next.effective_start;
      const end = next.effective_end;
      if (
        typeof start === 'string' &&
        typeof end === 'string' &&
        start >= end
      ) {
        // Invalid range: keep the edit local, drop any queued save so a stale
        // valid blob doesn't overwrite newer local state.
        cancelDebounce('patient_insurance');
        return;
      }
      if (debounced) {
        debouncedSave('patient_insurance', next);
      } else {
        // Every save carries the whole blob, so a queued debounced save is a
        // strict subset of this one — cancel it so the two PUTs can't race
        // and land stale-last on the server.
        cancelDebounce('patient_insurance');
        void saveField('patient_insurance', next);
      }
    },
    [cancelDebounce, debouncedSave, saveField]
  );

  const handleFieldChange = useCallback(
    <K extends keyof PatientInsuranceFormState>(
      key: K,
      value: PatientInsuranceFormState[K]
    ) => {
      const sectionKey = key as SectionKey;
      const next = {
        ...overridesRef.current,
        [sectionKey]: serializeFieldValue(sectionKey, value),
      };
      overridesRef.current = next;
      setDraft(d => ({ ...d, [sectionKey]: value }));
      setOverrides(next);
      persist(next, DEBOUNCED_KEYS.has(sectionKey));
    },
    [persist]
  );

  // Flush a pending debounced edit on blur so tabbing through the form
  // persists promptly (mirrors the tab's own field-blur behavior). The hook
  // owns the timer, so asking it avoids a stale local pending flag after the
  // timer fires on its own.
  const handleFieldBlur = useCallback(() => {
    if (!hasPendingDebounce('patient_insurance')) return;
    persist(overridesRef.current, false);
  }, [hasPendingDebounce, persist]);

  // Newest eligibility check that returned any plan/group/effective data.
  const latestEligibilityWithData = useMemo(() => {
    return [...pastEligibilityResults]
      .sort(
        (a, b) =>
          new Date(b.request_date).getTime() -
          new Date(a.request_date).getTime()
      )
      .find(
        r =>
          r.plan_name ||
          r.group_number ||
          r.effective_date ||
          r.termination_date
      );
  }, [pastEligibilityResults]);

  const handleApplyFromEligibility = useCallback(() => {
    const result = latestEligibilityWithData;
    if (!result) return;
    const next = { ...overridesRef.current };
    if (result.plan_name) next.plan_name = result.plan_name;
    if (result.group_number) next.group_number = result.group_number;
    if (result.effective_date) next.effective_start = result.effective_date;
    if (result.termination_date) next.effective_end = result.termination_date;
    // Drop applied keys from the draft so the display reflects the applied
    // blob values instead of stale hand-typed ones.
    overridesRef.current = next;
    setDraft(d => {
      const rest = { ...d };
      delete rest.plan_name;
      delete rest.group_number;
      delete rest.effective_start;
      delete rest.effective_end;
      return rest;
    });
    setOverrides(next);
    persist(next, false);
  }, [latestEligibilityWithData, persist]);

  if (!selectedIntake) return null;

  return (
    <CollapsibleSection
      title='Patient Insurance Details'
      // Uncontrolled; only the mount-time value matters, and the parent
      // remounts per intake, so a record with stored overrides opens expanded.
      defaultOpen={Object.keys(overrides).length > 0}
    >
      <div className='space-y-4'>
        <div className='text-xs text-gray-700'>
          Used when this intake is pushed to Roger. Untouched fields follow the
          patient information above.
        </div>
        {latestEligibilityWithData && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleApplyFromEligibility}
          >
            <Zap className='mr-2 h-3 w-3' />
            Apply from latest eligibility check
          </Button>
        )}
        {dateRangeInvalid && (
          <div className='text-xs text-red-600'>
            Effective start date must be before end date.
          </div>
        )}
        {ssnIncomplete && (
          <div className='text-xs text-red-600'>
            Insured SSN must be 9 digits; it will be left blank when this intake
            is pushed to Roger.
          </div>
        )}
        <PatientInsuranceFields
          form={displayForm}
          onFieldChange={handleFieldChange}
          onFieldBlur={handleFieldBlur}
          errors={errors}
          hiddenFields={HIDDEN_FIELDS}
          idPrefix='intake_insurance_'
        />
      </div>
    </CollapsibleSection>
  );
};
